/**
 * @file 加入知识库弹窗 — 空间/团队/KB 数据源 Hook（对齐 WeKnora）
 *
 * | 场景 | WeKnora 源 | 规则 |
 * |------|-----------|------|
 * | 加入现有 | useUploadKbCatalog (document) | GET /knowledge-bases 不传 X-Tenant-ID，按 tenant_id 分组 |
 * | 新建 KB | useKbSpaceOptions | memberships 排除 home tenant，role ∈ owner/admin/contributor |
 * | 提交 tenant | CreateActionModal | KB.tenantId → 所选团队 → 个人 tenant |
 */
import { useCallback, useMemo } from 'react';
import { useRequest } from 'ahooks';
import http from '@/utils/http';
import { useRequestController } from '@/pages/aigc/pages/Agent/Home/hooks/useRequestController';
import type {
  IKnowledgeBaseItem,
  ITeamOption,
} from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';
import {
  getPersonalTenantId,
  parseAuthMemberships,
  buildUploadKbCatalog,
  getCreatableTeamOptions,
  resolveExistingDocTenantId,
  resolveCreateKbTenantId,
  KB_ADD_MODAL_CATALOG_CACHE_KEY,
  KB_ADD_MODAL_AUTH_ME_CACHE_KEY,
  KB_ADD_MODAL_CACHE_MS,
} from '../utils/addModalSpaceUtils';
import type { TKnowledgeSpace } from '../components/KbAddFormControls';
import type { TKnowledgeAddTab } from './useKnowledgeBaseAddFormConfig';

export interface IUseKnowledgeBaseAddModalSpaceOptions {
  visible: boolean;
  activeTab: TKnowledgeAddTab;
  space: TKnowledgeSpace;
  selectedTeamId: string;
}

export const useKnowledgeBaseAddModalSpace = (
  options: IUseKnowledgeBaseAddModalSpaceOptions,
) => {
  const { visible, activeTab, space, selectedTeamId } = options;

  const { data: authRaw, loading: authLoading } = useRequestController({
    method: 'GET',
    url: '/api/kb/v1/auth/me',
    ready: visible,
    cacheKey: KB_ADD_MODAL_AUTH_ME_CACHE_KEY,
    staleTime: KB_ADD_MODAL_CACHE_MS,
    cacheTime: KB_ADD_MODAL_CACHE_MS,
    httpCfg: { rawData: true, ignoreCode: true },
  });

  const personalTenantId = useMemo(
    () => getPersonalTenantId(authRaw),
    [authRaw],
  );
  const memberships = useMemo(() => parseAuthMemberships(authRaw), [authRaw]);

  const fetchAllKnowledgeBases = useCallback(
    () =>
      http('/api/kb/v1/knowledge-bases', {
        method: 'GET',
        rawData: true,
        ignoreCode: true,
      }),
    [],
  );

  const {
    data: kbRaw,
    loading: catalogLoading,
  } = useRequest(fetchAllKnowledgeBases, {
    ready: visible,
    cacheKey: KB_ADD_MODAL_CATALOG_CACHE_KEY,
    staleTime: KB_ADD_MODAL_CACHE_MS,
    cacheTime: KB_ADD_MODAL_CACHE_MS,
  });

  const catalog = useMemo(
    () => buildUploadKbCatalog(kbRaw, personalTenantId, memberships),
    [kbRaw, personalTenantId, memberships],
  );

  const existingTeamOptions = useMemo<ITeamOption[]>(
    () => catalog.teamGroups.map((g) => ({ value: g.teamId, text: g.teamName })),
    [catalog.teamGroups],
  );

  const newTeamOptions = useMemo(
    () => getCreatableTeamOptions(memberships, personalTenantId),
    [memberships, personalTenantId],
  );

  const teamOptions = activeTab === 'existing' ? existingTeamOptions : newTeamOptions;

  const kbOptions = useMemo((): IKnowledgeBaseItem[] => {
    if (activeTab !== 'existing') return [];
    if (space === 'personal') return catalog.personalKbs;
    if (!selectedTeamId) return [];
    return catalog.teamGroups.find((g) => g.teamId === selectedTeamId)?.kbs ?? [];
  }, [activeTab, space, selectedTeamId, catalog]);

  const resolveSubmitTenantId = useCallback(
    (tab: TKnowledgeAddTab, kbId?: string): string | undefined => {
      if (tab === 'existing' && kbId) {
        return resolveExistingDocTenantId(
          kbId,
          catalog,
          space,
          selectedTeamId || undefined,
          personalTenantId,
        );
      }
      return resolveCreateKbTenantId(
        space,
        selectedTeamId || undefined,
        personalTenantId,
      );
    },
    [catalog, space, selectedTeamId, personalTenantId],
  );

  return {
    loading: authLoading || catalogLoading,
    teamOptions,
    kbOptions,
    resolveSubmitTenantId,
  };
};
